import {useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {expenseActions} from '../../state/actions/expense.actions';
import {bulkApproveHelpers, expensesHelpers, reviewHelpers} from '../../utils/helpers';
import {useIsAdminOrManager} from '../access/useIsAdminOrManager';
import {DEFAULT_MODAL_PROPS} from '../../constants';
import {logFlowEvent} from '../../snippets/firebase/analytics';

const defaultModalProps = {
  ...DEFAULT_MODAL_PROPS,
  expenses: [],
  inPolicyCount: 0,
  inPolicyTotals: [],
  approved: null,
  undoLoading: false,
  isOverride: false,
  outOfPolicyTotals: [],
  overageTotals: [],
  selected: [],
  error: {message: null, open: false},
};

// Bulk approve answers with the ids it actually approved; fall back to the requested ones if it sends none.
const getApprovedIds = (response, requestedIds) => (
  response?.expense_ids?.length ? response.expense_ids : requestedIds
);

/**
 * Bulk approve for a table selection. One click approves every in-policy expense at once. If any expense
 * is outside the policy, the pop-up then opens for those alone: confirming approves them in full, Keep in
 * review leaves them in review. If the current user is not the only assigned approver of some selected
 * expense, the pop-up opens first instead, with an override notice, and confirming approves the whole
 * selection. Bulk never approves part of an expense.
 * Each tab says how its table changes through the callbacks, because To Review removes rows and All keeps them.
 * @param {Object} params - Hook parameters.
 * @param {Object[]} params.selectedExpenses - The selected expense rows (no group rows).
 * @param {Function} params.onApproved - Called with `{ids, rows}` once the server approved them: update the table.
 * @param {Function} params.onUndone - Called with `{ids, rows}` after Undo reopened them: restore the table.
 * @param {Function} params.onSelectionChange - Called with the ids that should stay selected (empty after an approve).
 * @param {Function} [params.onSuccess] - Called after every successful approve call (refresh counters, next page).
 * @returns {{handleBulkApprove: Function, loading: boolean, selectionExtraText: (string|undefined), modalProps: Object}} What the tab renders and wires up.
 */
export const useBulkApprove = ({selectedExpenses, onApproved, onUndone, onSelectionChange, onSuccess}) => {
  const dispatch = useDispatch();
  const [t] = useTranslation(['main', 'expenses']);
  const hasAdminAccess = useIsAdminOrManager();
  const employeeEmail = useSelector((state) => state.user?.employee?.email ?? null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(defaultModalProps);

  const split = useMemo(() => bulkApproveHelpers.splitSelection(selectedExpenses), [selectedExpenses]);

  const selectionExtraText = split.outOfPolicy.length
    ? t('bulkApprove.selectionOutOfPolicy', {ns: 'expenses', count: split.outOfPolicy.length})
    : undefined;

  const reopen = ({ids, rows, onFinished}) => {
    dispatch(expenseActions.bulkReopenExpenses({
      data: {expense_ids: ids},
      successCallback: () => {
        onUndone({ids, rows});
        onFinished?.(true);
      },
      errorCallback: (err) => {
        expensesHelpers.handleExpenseError({response: err, fallbackKey: 'undoActionError', t});
        onFinished?.(false);
      },
    }));
  };

  const showApprovedAlert = ({ids, rows}) => {
    expensesHelpers.showDismissUndoAlert({
      translation: t,
      message: `${t('expensesWithCount', {count: ids.length})} ${t('expenseReviewStatuses.approved').toLowerCase()}`,
      onClick: () => reopen({ids, rows}),
    });
  };

  const approve = ({rows, method, note, remainingIds = [], showAlert = true, onDone, onFail}) => {
    const ids = rows.map(r => r.id);
    dispatch(expenseActions.bulkApproveExpenses({
      data: {expense_ids: ids, ...(note && {comment: note})},
      successCallback: (response) => {
        const approvedIds = getApprovedIds(response, ids);
        const approvedRows = rows.filter(r => approvedIds.includes(r.id));
        logFlowEvent('flow_completed', 'approve_expense', {action: 'approved', method, expense_count: approvedIds.length});
        onDone({ids: approvedIds, rows: approvedRows});
        onApproved({ids: approvedIds, rows: approvedRows});
        onSelectionChange(remainingIds);
        onSuccess?.();
        if (showAlert) showApprovedAlert({ids: approvedIds, rows: approvedRows});
      },
      errorCallback: onFail,
    }));
  };

  // Without an override the in-policy rows are already approved, so the pop-up only decides the rest.
  // With an override nothing is approved yet: the pop-up asks once, then approves the whole selection.
  const openModal = ({approvedIds = [], approvedRows = [], isOverride = false} = {}) => {
    setModal({
      ...defaultModalProps,
      open: true,
      isOverride,
      expenses: split.outOfPolicy,
      inPolicyCount: isOverride ? split.inPolicy.length : approvedRows.length,
      inPolicyTotals: isOverride ? split.inPolicyTotals : bulkApproveHelpers.splitSelection(approvedRows).allTotals,
      outOfPolicyTotals: split.outOfPolicyTotals,
      overageTotals: split.overageTotals,
      selected: isOverride ? selectedExpenses : split.outOfPolicy,
      approved: approvedRows.length ? {ids: approvedIds, rows: approvedRows} : null,
    });
  };

  const approveSelection = () => {
    if (!split.inPolicy.length) {
      openModal();
      return;
    }
    setLoading(true);
    approve({
      rows: split.inPolicy,
      method: 'bulk',
      remainingIds: split.outOfPolicy.map(r => r.id),
      // When the pop-up opens for the rest, it carries the Undo for these, so no separate alert.
      showAlert: !split.outOfPolicy.length,
      onDone: ({ids, rows}) => {
        setLoading(false);
        if (split.outOfPolicy.length) openModal({approvedIds: ids, approvedRows: rows});
      },
      onFail: (err) => {
        setLoading(false);
        expensesHelpers.handleExpenseError({response: err, fallbackKey: 'bulkActionError', t});
      },
    });
  };

  const handleBulkApprove = () => {
    if (!selectedExpenses.length) return;
    // Same rule as the Approve button on a table row: an assigned approver who has not approved yet is
    // not overriding anyone, whatever else is on the approval list.
    const needsOverride = selectedExpenses.some(expense => {
      const isAssignedAndPending = reviewHelpers.isEmployeeInApprovalList(expense, employeeEmail)
        && !reviewHelpers.isAlreadyApprovedByEmployee(expense, employeeEmail);
      const adminOverride = isAssignedAndPending ? false : hasAdminAccess;
      return reviewHelpers.getApproveMode({data: expense, employeeEmail, isAdmin: hasAdminAccess, adminOverride}) === 'override';
    });
    if (needsOverride) {
      openModal({isOverride: true});
      return;
    }
    approveSelection();
  };

  const handleKeepInReview = () => {
    if (modal.loading) return;
    setModal(prev => ({...prev, open: false}));
  };

  const handleUndoApproved = () => {
    if (!modal.approved || modal.undoLoading) return;
    const {ids, rows} = modal.approved;
    setModal(prev => ({...prev, undoLoading: true}));
    reopen({
      ids,
      rows,
      onFinished: (undone) => setModal(prev => ({
        ...prev,
        undoLoading: false,
        ...(undone && {approved: null, inPolicyCount: 0, inPolicyTotals: []}),
      })),
    });
  };

  const handleApproveOutOfPolicy = ({note}) => {
    if (!modal.selected.length) return;
    setModal(prev => ({...prev, loading: true, error: {message: null, open: false}}));
    approve({
      rows: modal.selected,
      method: modal.isOverride ? 'bulk_override' : 'bulk_out_of_policy',
      note,
      onDone: () => setModal(prev => ({...prev, open: false, loading: false})),
      onFail: (err) => {
        const message = err?.data?.message || err?.response?.data?.message || t('expenses:validation.bulkActionError');
        setModal(prev => ({...prev, loading: false, error: {message, open: true}}));
      },
    });
  };

  return {
    handleBulkApprove,
    loading,
    selectionExtraText,
    modalProps: {...modal, onApprove: handleApproveOutOfPolicy, onKeep: handleKeepInReview, onUndo: handleUndoApproved},
  };
};
